import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useContext, useEffect, useMemo, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../common/helpers/isEmpty";
import {
  PHONE_NUMBER_REGEX,
  VALID_EMAIL_REGEX,
} from "../../../common/helpers/regexes";
import { Talent, talentIsDeactivated } from "../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentAccount,
} from "../../talents/talentsSlice";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { FormDobRow } from "./FormDobRow";
import FormLocationRow from "./FormLocationRow";
import FormNameRow from "./FormNameRow";
import FormPhoneNumber from "./FormPhoneNumber";
import FormRow from "./FormRow";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";

const TalentEditPersonalInfo = () => {
  const { t } = useTranslation("talentProfile");
  const [edit, setEdit] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const { canEditTalentProfile } = useContext(AccessRightContext);

  const {
    address,
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    version,
    dob,
  } = talent.account;

  const defaultValues = useMemo(
    () => ({
      firstName,
      lastName,
      dob: dob ? new Date(dob) : null,
      emailAddress,
      phoneNumber,
      street: address?.street,
      zip: address?.zip,
      city: address?.city,
      country: address?.country,
    }),
    [address, firstName, lastName, dob, emailAddress, phoneNumber],
  );

  const methods = useForm({ defaultValues, mode: "onChange" });

  const {
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors, isDirty, isSubmitted },
  } = methods;

  const dispatch = useAppDispatch();

  // Watch form values to force re-render when any field changes
  const watchedValues = watch();

  const saveDisabled = useMemo(
    () => !isEmpty(errors) || !isDirty,
    // WatchedValues is needed to force re-render when any field changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors, isDirty, watchedValues],
  );

  useEffect(() => {
    methods.trigger();
  }, [methods]);

  useEffect(() => {
    if (saveDisabled && getValues("phoneNumber")) {
      setShowTooltip(!!errors.phoneNumber);
    } else {
      setShowTooltip(false);
    }
  }, [errors, saveDisabled, getValues, isDirty]);

  // for some reason this reset is needed to reset isDirty
  // This helps after submit to reset default values
  useEffect(() => {
    if (isSubmitted) {
      reset(getValues());
    }
  }, [isSubmitted, reset, getValues]);

  const onSubmit = async (data: FieldValues) => {
    const {
      firstName,
      lastName,
      dob,
      emailAddress,
      phoneNumber,
      street,
      zip,
      city,
      country,
    } = data;

    const formattedDob = format(dob, "yyyy-MM-dd");

    const account = {
      firstName,
      lastName,
      dob: formattedDob,
      emailAddress,
      phoneNumber,
      version,
      talentId: talent.id,
      address: {
        street,
        city,
        zip,
        country,
      },
    };

    dispatch(updateTalentAccount({ ...account })).then((action: any) => {
      if (isFulfilled(action)) {
        setEdit(false);
        return true;
      }
    });
  };

  const onSave = () => {
    if (!edit) setEdit(true);
    if (edit) handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    setEdit(false);
    reset(defaultValues);
  };

  const combineValues = (type: "name" | "location") => {
    const values =
      type === "name"
        ? getValues(["firstName", "lastName"])
        : getValues(["zip", "city"]);
    return values.join(" ");
  };

  return (
    <FormSection
      title={t("talentProfile:personalInformation")}
      margin
      noBorder
      edit={edit}
      onEdit={onSave}
      onCancel={onCancel}
      saveDisabled={saveDisabled}
      readOnly={!canEditTalentProfile || talentIsDeactivated(talent)}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <FormNameRow edit={edit} value={combineValues("name")} />
          <FormDobRow maxWidth="135px" edit={edit} />
          <FormRow
            name="emailAddress"
            rules={{
              required: { value: true, message: "" },
              pattern: { value: VALID_EMAIL_REGEX, message: "" },
            }}
            maxWidth="372px"
            // Disable editing for Vaihe 1
            edit={false}
          />
          <FormPhoneNumber
            name="phoneNumber"
            rules={{
              required: { value: true, message: "" },
              pattern: { value: PHONE_NUMBER_REGEX, message: "" },
            }}
            maxWidth="160px"
            edit={edit}
            showTooltip={showTooltip}
          />
          <FormRow
            name="street"
            rules={{ required: { value: true, message: "" } }}
            maxWidth="372px"
            edit={edit}
          />
          <FormLocationRow edit={edit} value={combineValues("location")} />
        </UnsavedChangesProvider>
      </FormProvider>
    </FormSection>
  );
};

export default TalentEditPersonalInfo;
